import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, map, take } from 'rxjs';
import { PfmV2Service } from '@shared/services/base/pfm-v2.service';
import {  ManageTableViewConfig } from '@manage-ceg/types/manage-ceg-page.types';
import { createDeepCopy } from '@shared/utils/common.utils';
import ManageCegTableContainerStore, { MANAGE_PAGE_FIELDS } from '@manage-ceg/components/manage-ceg-table-container/manage-ceg-table-container.store';
import { UtilityService } from '@shared/services/utility.service';
import { TableColumnId } from '@manage-ceg/components/manage-ceg-table-container/manage-ceg-table-container.enum';
import { PfmV3Service } from '@shared/services/base/pfm-v3.service';
import { LocalStorageService } from '@common-lib/services/local-storage.service';

export interface CustomAvailableBudgetFormulationDO {
  id: string,
  name:string,
  upd: string,
  crd: string,
  budget: string | null,
  display_order: number,
  manage_page_fields: number,
}

@Injectable({
  providedIn: 'root'
})
export class CustomAvailableBudgetConfigurationService {

  private api_v2_manager = inject(PfmV2Service);
  private api_v3_manager = inject(PfmV3Service);
  private utilityService = inject(UtilityService);

  private urls = {
    getCABFormula: () => `budget_cab_formula/`
  }
  public readonly loadAllRowsData = new BehaviorSubject<boolean>(false);
  public loadAllRowsData$ = this.loadAllRowsData.asObservable();

  private readonly managePageCustomAvailableBudgetFormulaOverride = new BehaviorSubject<boolean>(false);
  public managePageCustomAvailableBudgetFormulaOverride$ = this.managePageCustomAvailableBudgetFormulaOverride.asObservable();

  private readonly managePageCustomAvailableBudgetFormulaItems = new BehaviorSubject<string[]>([]);
  public managePageCustomAvailableBudgetFormulaItems$ = this.managePageCustomAvailableBudgetFormulaItems.asObservable();

  public readonly budgetDefinedCABFormula = new BehaviorSubject<Array<{ id: number, name: string }>>([]);
  public budgetDefinedCABFormula$ = this.budgetDefinedCABFormula.asObservable();

  constructor(private http: HttpClient) { }

 
  public reloadRows() {
    this.loadAllRowsData.next(true);
  }

  public setManagePageCustomAvailableBudgetFormulaOverride(val: boolean) {
    this.managePageCustomAvailableBudgetFormulaOverride.next(val);
  }

  public setManagePageCustomAvailableBudgetFormulaItems(items: string[]) {
    this.managePageCustomAvailableBudgetFormulaItems.next(items);
  }

  public getCustomAvailableBudgetFormulation(companyId: string, budgetId: string) {

    return this.api_v3_manager.get(
      this.urls.getCABFormula(), { 
        company_id: companyId,
        budget: budgetId 
      }
  ).pipe(
      take(1),
      map((response: string[]) => {
        const mpFields: Array<Record<string, any>> = LocalStorageService.getFromStorage(MANAGE_PAGE_FIELDS);
        const budgetCABFormulaItems = response.map(item => {
          const mpField = mpFields.find(field => field.key_code === item);
          return {
            id: mpField?.id,
            name: mpField?.name
          };
        });
        this.budgetDefinedCABFormula.next(budgetCABFormulaItems);
        this.setManagePageCustomAvailableBudgetFormulaItems(budgetCABFormulaItems.map(item => item.name));
        return budgetCABFormulaItems;
      })
    );
  }

  public transformDefaultTableConfigDataFromAPIToDialogState(defaultViewConfig: ManageTableViewConfig) {
    const columnsToShow = this.budgetDefinedCABFormula.getValue().map(item => item.id);
    const defaultCABManagePageViewConfig = createDeepCopy(defaultViewConfig);

    columnsToShow.forEach(id => {
      const property = this.getColumnPropertyFromId(id);
      if (property) {
        defaultCABManagePageViewConfig.columns[property] = true;
      }
      delete defaultCABManagePageViewConfig.columns.committedAndPlanned;
    });

    return defaultCABManagePageViewConfig
  }

  public checkBudgetFormulaWithUserPreferenceCABFormula(columnsToShow: string[]) {
    const isManagePageCABFormulaOverriden = !this.utilityService.areEqualSets(columnsToShow, this.budgetDefinedCABFormula.getValue().map(item => item.id).concat(TableColumnId.Available))
    this.setManagePageCustomAvailableBudgetFormulaOverride(isManagePageCABFormulaOverriden);
  }

  public getColumnPropertyFromId(id): string {
      const map = ManageCegTableContainerStore.dbId2TableColumnNameMap;
      for (const key in map) {
        if (map[key] == id) {
          return key;
        }
      }
  }

  public getBudgetDefinedCABFormulaItemsLabel() {
    return this.budgetDefinedCABFormula.getValue().map(item => item.name);
  }


}
